import axios from 'axios';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import PhoneInput from 'react-phone-input-2';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '../../store';
import { currentUSStateCodes, replacementFields } from 'utils/constants';
import { countdown, generateCSV, parseError } from 'utils/helpers';

import Loader from 'components/Base/Loader';
import BaseEdit from 'components/Base/BaseEdit';
import Completed from 'components/Base/Completed';
import BaseCheckbox from 'components/Base/BaseCheckbox';
import ConfirmPhone from './ConfirmPhone';
import BaseModal from 'components/Base/BaseModal';
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode';

const VISIT_REASONS = [
	{ id: 'grief', label: 'Coping with a loss or grief' },
	{ id: 'lonely', label: 'Feeling lonely or isolated' },
	{ id: 'health', label: 'Changes in health or mobility' },
	{ id: 'anxious', label: 'Feeling anxious or worried' },
	{ id: 'talk', label: 'Just need someone to talk to' },
	{ id: 'other', label: 'Other' }
];

const TabGeneral = () => {
	const avatarPrev = useRef();
	const avatarInput = useRef();
	const [avatarChanged, setAvatarChanged] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSaved, setIsSaved] = useState(false);
	const [isSavedField, setIsSavedField] = useState({});
	const [error, setError] = useState({});
	const [isCodeSent, setIsCodeSent] = useState(false);
	const [isAddressVerifying, setIsAddressVerifying] = useState(false);

	const [generalInfo, setGeneralInfo] = useState({
		name: '',
		surname: '',
		dob: '',
		location: {
			addressLine1: '',
			addressLine2: '',
			city: '',
			state: '',
			zipCode: '',
			country: 'US',
			countryCode: '',
		},
		about: '',
		phone: '',
		reasonsForVisit: [],
		otherReason: '',
		homeAccessInstructions: '',
		autoCalendarEvent: false,
		isGoogleConnected: false,
		isPhoneVerified: false,
		isAddressVerified: false,
		emailMessageNotify: false,
		emailNotifications: false,
		termsAccepted: false
	});

	const [prevGeneralInfo, setPrevGeneralInfo] = useState({});
	const store = useStore();
	const navigate = useNavigate();
	const [showOtpModal, setShowOtpModal] = useState(false);
	const [phoneCode, setPhoneCode] = useState('');
	const [timeLeft, setTimeLeft] = useState(0);

	useEffect(() => {
		axios
			.get('/profile/general')
			.then(({ data }) => {
				if (data.dob) {
					data.dob = new Date(data.dob).toISOString().split('T')[0];
				}
				const mergedData = {
					name: data.name || '',
					surname: data.surname || '',
					dob: data.dob || '',
					location: {
						addressLine1: data.location?.addressLine1 || '',
						addressLine2: data.location?.addressLine2 || '',
						city: data.location?.city || '',
						state: data.location?.state || '',
						zipCode: data.location?.zipCode || '',
						country: data.location?.country || '',
						countryCode: data.location?.countryCode || '',
					},
					about: data.about || '',
					phone: data.phone || '',
					reasonsForVisit: data.reasonsForVisit || [],
					otherReason: data.otherReason || '',
					homeAccessInstructions: data.homeAccessInstructions || '',
					autoCalendarEvent: data.autoCalendarEvent ?? false,
					isGoogleConnected: data.isGoogleConnected ?? false,
					isPhoneVerified: data.isPhoneVerified ?? false,
					isAddressVerified: data.isAddressVerified ?? false,
					emailMessageNotify: data.emailMessageNotify ?? false,
					emailNotifications: data.emailNotifications ?? false,
					termsAccepted: data.termsAccepted ?? false,
				};
				setGeneralInfo(mergedData);
				setPrevGeneralInfo(mergedData);
			})
			.catch((error) => {
				console.error("Error fetching general profile info:", error);
			});
	}, []);

	useEffect(() => {
		if (store.auth.user.avatar && !avatarPrev.current) {
			avatarPrev.current = store.auth.user.avatar;
		}
	}, [store.auth.user.avatar]);

	useEffect(() => {
		return () => {
			store.auth.changeAvatar(avatarPrev.current);
		};
	}, [store.auth]);

	const onAvatarChange = useCallback(
		(event) => {
			if (event.target.files.length) {
				const file = event.target.files[0];
				const image = URL.createObjectURL(file);
				store.auth.changeAvatar(image);
				setAvatarChanged(true);
			}
		},
		[store.auth]
	);

	const saveAvatar = useCallback(() => {
		const file = avatarInput.current.files[0];
		const form = new FormData();
		form.append('avatar', file);

		setIsLoading(true);

		axios
			.patch('/profile/avatar', form)
			.then(({ data }) => {
				avatarPrev.current = data.avatar;
				avatarInput.current.value = '';
				store.auth.changeAvatar(data.avatar);
				setAvatarChanged(false);
				setIsSaved(true);
			})
			.catch((error) => {
				console.error("Error saving avatar:", error);
				setIsSaved(false);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [store.auth]);

	const createGoogleToken = useGoogleLogin({
		onSuccess: credentialResponse => {
			createToken(credentialResponse.code)
		},
		flow: 'auth-code',
		onError: (e) => console.log('Google error', e),
		cookiePolicy: 'single_host_origin',
		responseType: 'code',
		accessType: 'offline',
		scope: 'openid email profile https://www.googleapis.com/auth/calendar'
	});

	const createToken = (token) => {
		axios.post('/profile/connectcalendar', { token })
			.then((response) => {
				console.log("Token created successfully:", response.data);
			})
			.catch((error) => {
				console.error("Error creating token:", error);
			});
	}

	const toggleGoogleNotification = (e) => {
		setGeneralInfo(prevForm => ({ ...prevForm, autoCalendarEvent: e.target.checked }));
		saveChanges("autoCalendarEvent", e.target.checked);
	};

	const toggleEmailMessages = (e) => {
		setGeneralInfo(prevForm => ({ ...prevForm, emailMessageNotify: e.target.checked }));
		saveChanges("emailMessageNotify", e.target.checked);
	}

	const toggleEmailNotifications = (e) => {
		setGeneralInfo(prevForm => ({ ...prevForm, emailNotifications: e.target.checked }));
		saveChanges("emailNotifications", e.target.checked);
	}

	const toggleMessageNotifications = (e) => {
		setGeneralInfo(prevForm => ({ ...prevForm, messageNotify: e.target.checked }));
		saveChanges("messageNotify", e.target.checked);
	}

	const toggleTermsAccepted = (e) => {
		const isChecked = e.target.checked;
		setGeneralInfo(prevForm => ({
			...prevForm,
			termsAccepted: isChecked,
			messageNotify: isChecked ? prevForm.messageNotify : false
		}));

		saveChanges("termsAccepted", isChecked);
		if (!isChecked) {
			saveChanges("messageNotify", false);
		}
	}

	const toggleReason = (reasonId) => {
		setGeneralInfo(prevForm => {
			const isSelected = prevForm.reasonsForVisit.includes(reasonId);
			const newReasons = isSelected
				? prevForm.reasonsForVisit.filter(id => id !== reasonId)
				: [...prevForm.reasonsForVisit, reasonId];

			saveChanges("reasonsForVisit", newReasons);
			return { ...prevForm, reasonsForVisit: newReasons };
		});
	};

	const downloadCSV = () => {
		axios.get('/profile/jobhistory').then(({ data }) => {
			const output = generateCSV(data);
			const blob = new Blob([output], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.setAttribute('href', url);
			a.setAttribute('download', 'MyJobHistory.csv');
			a.click();
		}).catch((error) => {
			setError({ type: "error", message: "Failed to download CSV. Please try again later." })
		});
	};

	const compareChanges = (e) => {
		const { name, value } = e.target;
		if (prevGeneralInfo.location[name] === value || prevGeneralInfo[name] === value) return
		saveChanges(name, value);
	}

	const comparePhoneNumber = (e) => {
		if (store.auth.user.isPartner) {
			setShowOtpModal(true);
			setIsCodeSent(false);
			return;
		}
		setGeneralInfo(prevForm => ({
			...prevForm,
			isPhoneVerified: false
		}));
		if (prevGeneralInfo.phone === e.target.value) return;
		saveChanges("phone", e.target.value.replace(/\D/g, ''));
	}

	const validatePhoneNumber = async ({ resend }) => {
		try {
			setError({})
			await axios.post("/auth/signup/number/check", {
				resend,
				number: generalInfo.phone
			});

			setTimeLeft(60);
			countdown(setTimeLeft);
			setIsCodeSent(true);
		} catch (error) {
			setError(parseError(error));
			console.error("Failed to send OTP:", error);
		}
	};

	const confirmPhoneValidateCode = async () => {
		try {
			await axios.post('/auth/signup/number/confirm', {
				code: phoneCode,
				number: generalInfo.phone
			});
			setShowOtpModal(false);
			setIsCodeSent(false);
			setTimeLeft(0);
			saveChanges("phone", generalInfo.phone.replace(/\D/g, ''));
			setGeneralInfo(prevForm => ({
				...prevForm,
				isPhoneVerified: true
			}));
			store.auth.updateStringsToFill("isPhoneVerified", false);
			checkProfileCompletion();
		} catch (error) {
			setError(parseError(error));
			console.error("Validation failed", error);
		}
	};

	const saveChanges = (name, value) => {
		setError({});
		setIsLoading(true);
		let field = name;
		let previousValue = prevGeneralInfo[name];
		if (generalInfo[name] === undefined && name !== 'reasonsForVisit') {
			field = `location.${name}`;
			previousValue = prevGeneralInfo.location[name];
			setGeneralInfo(prevForm => ({
				...prevForm,
				isAddressVerified: false
			}));
		}

		axios
			.patch('/profile/general', { field, value })
			.then(({ data }) => {
				if (field === "name" || field === "surname") {
					store.auth.changeNames(generalInfo.name, generalInfo.surname);
				}
				if (prevGeneralInfo[name] === undefined && name !== 'reasonsForVisit') {
					setPrevGeneralInfo((prev) => ({
						...prev,
						location: {
							...prev.location,
							[name]: value,
						},
					}));
				} else {
					setPrevGeneralInfo((prev) => ({
						...prev,
						[name]: value,
					}));
				}
				if (field.startsWith('location.')) {
					const locationField = field.split('.')[1];
					store.auth.updateStringsToFill(locationField, !value);
				} else {
					store.auth.updateStringsToFill(field, !value);
				}

				setIsSavedField({ [field]: true });;
				checkProfileCompletion();

				setTimeout(() => {
					setIsSavedField({});
				}, 900);
			}).catch((error) => {
				setError(parseError(error));
				if (generalInfo[name] === undefined && name !== 'reasonsForVisit') {
					setGeneralInfo(prevForm => ({
						...prevForm,
						location: { ...prevForm.location, [name]: previousValue },
					}));
				} else {
					setGeneralInfo(prevForm => ({
						...prevForm,
						[name]: previousValue
					}));
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	const verifyAddress = () => {
		setIsAddressVerifying(true);
		setError({});

		axios
			.patch('/profile/verify/address', {}, { timeout: 30000 })
			.then(({ data }) => {
				setGeneralInfo(prevForm => ({
					...prevForm,
					isAddressVerified: true
				}))

				store.auth.updateStringsToFill("isAddressVerified", false)
				checkProfileCompletion()
			}).catch((error) => {
				setError({
					type: error.response?.data?.error || "Error",
					message: error.response?.data?.fields?.map(word => replacementFields[word || word]).join(', ') || "Verification failed"
				});
				console.log(error.response);
			})
			.finally(() => {
				setIsAddressVerifying(false);
			});
	}

	const checkProfileCompletion = useCallback(async () => {
		const pendingBooking = sessionStorage.getItem('pendingBooking');
		await store.auth.fetchUserInfo();

		if (pendingBooking && !store.auth.profile.stringsToFill.length) {
			const providerId = pendingBooking;
			sessionStorage.removeItem('pendingBooking');
			navigate(`/jobs/create/${providerId}`);
		}
	}, [store.auth.profile.stringsToFill, navigate]);

	return (
		<div className="edit-tab general">
			{!!store.auth.profile.stringsToFill.length && (
				<div className="profile-incomplete-notice">
					Please complete your profile to book jobs:
					<ul>
						{store.auth.profile.stringsToFill.map((field, index) => (
							<li key={index}>
								{replacementFields[field] || field}
							</li>
						))}
					</ul>
				</div>
			)}
			<div className="base-label">Avatar</div>
			<div className="avatar">
				<input
					type="file"
					className="base-input"
					ref={avatarInput}
					onChange={onAvatarChange}
				/>
				<button
					className="base-button upload"
					disabled={!avatarChanged}
					onClick={saveAvatar}
				>
					Upload
				</button>
			</div>
			<div className="custom-flex">
				<div className="w48">
					<div className='input-container'>
						<div className="base-label">Name</div>
						<input
							type="text"
							className={classNames('base-input', { saved: isSavedField.name })}
							name="name"
							value={generalInfo.name}
							onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, name: e.target.value }))}
							onBlur={compareChanges}
						/>
						<span className={classNames("saved-message", { show: isSavedField.name })}>
							Saved
						</span>
					</div>
				</div>
				<div className="w48">
					<div className="input-container">
						<div className="base-label">Surname</div>
						<input
							type="text"
							className={classNames('base-input', { saved: isSavedField.surname })}
							name='surname'
							value={generalInfo.surname}
							maxLength="30"
							onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, surname: e.target.value }))}
							onBlur={compareChanges}
						/>
						<span className={classNames("saved-message", { show: isSavedField.surname })}>
							Saved
						</span>
					</div>
				</div>
			</div>
			{store.auth.user.type === 'customer' && (
				<div className='input-container'>
					<div className="base-label">Date of Birth</div>
					<input
						type="date"
						className={classNames('base-input', { saved: isSavedField.dob })}
						name="dob"
						value={generalInfo.dob || ''}
						onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, dob: e.target.value }))}
						onBlur={compareChanges}
					/>
					<span className={classNames("saved-message", { show: isSavedField.dob })}>
						Saved
					</span>
				</div>
			)}
			<div className='input-container'>
				<div className="base-label">{store.auth.user.isPartner ? "Business phone" : "Phone"} number</div>
				<div className="phone-container">
					<PhoneInput
						className={classNames('phone-input', { save: isSavedField.phone })}
						country={'us'}
						specialLabel=''
						value={generalInfo.phone?.toString()}
						onChange={(phone) => setGeneralInfo(prevForm => ({ ...prevForm, phone: phone }))}
						onBlur={comparePhoneNumber}
					/>
					{!store.auth.user.isPartner && <ConfirmPhone onConfirmed={checkProfileCompletion} />}
				</div>
				<span className={classNames("saved-message", { show: isSavedField['phone'] })}>
					Saved
				</span>
			</div>

			{store.auth.user.isPartner && (
				<BaseModal
					show={showOtpModal}
					onClose={() => {
						setShowOtpModal(false);
						setIsCodeSent(false);
						setTimeLeft(0);
					}}
					className="center"
				>
					<div className="confirmation-container">
						<h2>Verify Your Phone Number</h2>
						<p>Please enter the confirmation code sent to your phone to validate your number.</p>
						{!isCodeSent ? (
							<button onClick={() => validatePhoneNumber({ resend: false })} className="base-button">
								Send code
							</button>
						) : (
							<>
								<ConfirmationCode setConfirm={setPhoneCode}>
									{error.message ? (
										<div className="error">
											<b>{error.type}:</b> {error.message}
										</div>
									) : (
										<div>
											{timeLeft > 0 ? (
												<div className="timer">Resend available in {timeLeft} seconds.</div>
											) : (
												<div className="resend">
													Did not receive a code?
													<button onClick={() => validatePhoneNumber({ resend: true })}>
														Resend
													</button>
												</div>
											)}
										</div>
									)}
								</ConfirmationCode>
								<button onClick={confirmPhoneValidateCode} className="base-button">
									Confirm
								</button>
							</>
						)}
					</div>
				</BaseModal>
			)}

			{!store.auth.user.isPartner &&
				<>
					<div className={classNames('input-container', { saved: isSavedField.about })}>
						<div className="base-label">About me</div>
						<BaseEdit
							name={'about'}
							value={generalInfo.about}
							onChange={(about) => setGeneralInfo(prevForm => ({ ...prevForm, about: about }))}
							onBlur={compareChanges}
						/>
						<span className={classNames("saved-message", { show: isSavedField.about })}>
							Saved
						</span>
					</div>

					{store.auth.user.type === 'customer' && (
						<>
							<p className="after-about">
								Your profile is your way of presenting yourself to potential caregivers
								that are going to work with you. This will help ensure you find the
								right match for your needs.
							</p>

							<div className="divider" />

							<div className='input-container'>
								<div className="base-label">Home Access Instructions (Optional)</div>
								<input
									type="text"
									className={classNames('base-input', { saved: isSavedField.homeAccessInstructions })}
									name="homeAccessInstructions"
									placeholder='e.g., "Park in the driveway", "Ring the side doorbell"'
									value={generalInfo.homeAccessInstructions}
									onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, homeAccessInstructions: e.target.value }))}
									onBlur={compareChanges}
								/>
								<span className={classNames("saved-message", { show: isSavedField.homeAccessInstructions })}>
									Saved
								</span>
							</div>

							<div className='input-container'>
								<div className="base-label">Reason for Visit</div>
								<div className="reasons-list">
									{VISIT_REASONS.map(reason => (
										<div key={reason.id} style={{ marginBottom: '8px' }}>
											<BaseCheckbox
												checked={generalInfo.reasonsForVisit.includes(reason.id)}
												onChange={() => toggleReason(reason.id)}
												text={reason.label}
											/>
										</div>
									))}
								</div>
								<span className={classNames("saved-message", { show: isSavedField.reasonsForVisit })}>
									Saved
								</span>
							</div>

							{generalInfo.reasonsForVisit.includes('other') && (
								<div className='input-container'>
									<input
										type="text"
										className={classNames('base-input', { saved: isSavedField.otherReason })}
										name="otherReason"
										placeholder="Please specify other reason"
										value={generalInfo.otherReason}
										onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, otherReason: e.target.value }))}
										onBlur={compareChanges}
									/>
									<span className={classNames("saved-message", { show: isSavedField.otherReason })}>
										Saved
									</span>
								</div>
							)}

							<div className="divider" />
						</>
					)}

					<div className='input-container'>
						<div className="base-label">Address Line 1</div>
						<input
							type="text"
							className={classNames('base-input', { saved: isSavedField['location.addressLine1'] })}
							name='addressLine1'
							maxLength="100"
							value={generalInfo.location.addressLine1}
							onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, location: { ...prevForm.location, addressLine1: e.target.value } }))}
							onBlur={compareChanges}
						/>
						<span className={classNames("saved-message", { show: isSavedField['location.addressLine1'] })}>
							Saved
						</span>
					</div>

					<div className='input-container'>
						<div className="base-label">Address Line 2</div>
						<input
							type="text"
							className={classNames('base-input', { saved: isSavedField['location.addressLine2'] })}
							name='addressLine2'
							maxLength="100"
							value={generalInfo.location.addressLine2}
							onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, location: { ...prevForm.location, addressLine2: e.target.value } }))}
							onBlur={compareChanges}
						/>
						<span className={classNames("saved-message", { show: isSavedField['location.addressLine2'] })}>
							Saved
						</span>
					</div>

					<div className="custom-flex">
						<div className='w24'>
							<div className='input-container'>
								<div className="base-label">City</div>
								<input
									type="text"
									className={classNames('base-input', { saved: isSavedField['location.city'] })}
									name='city'
									maxLength="30"
									value={generalInfo.location.city}
									onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, location: { ...prevForm.location, city: e.target.value } }))}
									onBlur={compareChanges}
								/>
								<span className={classNames("saved-message", { show: isSavedField['location.city'] })}>
									Saved
								</span>
							</div>
						</div>

						<div className='w24'>
							<div className='input-container'>
								<div className="base-label">State</div>
								<select
									id="state"
									name="state"
									className={classNames('base-input', { saved: isSavedField['location.state'] })}
									value={generalInfo.location.state}
									onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, location: { ...prevForm.location, state: e.target.value } }))}
									onBlur={compareChanges}
								>
									<option value=""> -- Select a state --</option>
									{Object.entries(currentUSStateCodes).map(([stateCode, stateName]) => (
										<option key={stateCode} value={stateCode}>{stateName}</option>
									))}
								</select>
								<span className={classNames("saved-message", { show: isSavedField['location.state'] })}>
									Saved
								</span>
							</div>
						</div>

						<div className='w24'>
							<div className='input-container'>
								<div className="base-label">Zip Code</div>
								<input
									type="text"
									className={classNames('base-input', { saved: isSavedField['location.zipCode'] })}
									name="zipCode"
									maxLength="10"
									value={generalInfo.location.zipCode}
									onInput={(e) => setGeneralInfo(prevForm => ({ ...prevForm, location: { ...prevForm.location, zipCode: e.target.value } }))}
									onBlur={compareChanges}
								/>
								<span className={classNames('saved-message', { show: isSavedField['location.zipCode'] })}>
									Saved
								</span>
							</div>
						</div>
						<button
							className='btn address-confirmation'
							disabled={(!['addressLine1', 'city', 'state', 'zipCode'].every(key => generalInfo.location[key]) || generalInfo.isAddressVerified || isAddressVerifying)}
							onClick={verifyAddress}
						>
							{isAddressVerifying ? "Validating address..." : (generalInfo.isAddressVerified ? "Address confirmed" : "Validate address")}
						</button>
					</div>

					<div className="divider" />

					<div className="base-label">Email Notifications</div>
					<div className="custom-flex">
						<div className="w48">
							<div className="input-container">
								<div className="base-label">Appointment updates</div>
								<BaseCheckbox
									onChange={toggleEmailNotifications}
									checked={generalInfo.emailNotifications}
									text={"Get notified about the changes to your appointments"}
								/>
								<span className={classNames("saved-message", { show: isSavedField.emailNotifications })}>
									Saved
								</span>
							</div>
						</div>

						<div className="w48">
							<div className="input-container">
								<div className="base-label">Chat Messages</div>
								<BaseCheckbox
									onChange={toggleEmailMessages}
									checked={generalInfo.emailMessageNotify}
									text={"Get notified when you receive new messages in chat"}
								/>
								<span className={classNames("saved-message", { show: isSavedField.emailMessageNotify })}>
									Saved
								</span>
							</div>
						</div>
					</div>

					<div className="divider" />

					<div className="w48">
						<div className="input-container">
							<div className="base-label">SMS Notifications</div>
							<BaseCheckbox
								onChange={toggleMessageNotifications}
								checked={generalInfo.messageNotify}
								text={"Receive SMS notifications"}
								disabled={generalInfo.termsAccepted ? false : true}
							/>
							<span className={classNames("saved-message", { show: isSavedField.messageNotify })}>
								Saved
							</span>

							<div className="mt-3 pt-2 border-t border-gray-200">
								<label className="flex items-start space-x-2 text-xs text-gray-600 cursor-pointer">
									<div className="flex items-start mt-0.5">
										<input
											type="checkbox"
											checked={generalInfo.termsAccepted}
											onChange={toggleTermsAccepted}
											className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
										/>
										<span className="ml-2 block text-xs text-gray-600 leading-tight">
											I agree to receive SMS notifications from Emissary Health. Message frequency may vary. Recurring messages. Message & data rates may apply. Reply STOP to unsubscribe, HELP for more info.
											By subscribing, I also agree to the{' '}
											<Link className="underline text-blue-600 hover:text-blue-700" to="/terms">Terms of Use</Link>{' '}
											and{' '}
											<Link className="underline text-blue-600 hover:text-blue-700" to="/policy">
												Privacy Policy
											</Link>
											{' '}
											for message notifications
										</span>
									</div>
								</label>
								<span className={classNames("saved-message", { show: isSavedField.termsAccepted })}>
									Saved
								</span>
							</div>
						</div>
					</div>

					<div className="divider" />
					<div className="base-label">Sync google calendar</div>
					{!generalInfo.isGoogleConnected &&
						<>
							<button className='btn' onClick={createGoogleToken}>Connect Google account</button>
							<br />
						</>
					}

					<div className='google-event'>
						{generalInfo.autoCalendarEvent !== undefined &&
							(<BaseCheckbox
								onChange={toggleGoogleNotification}
								checked={generalInfo.autoCalendarEvent}
								text={"Google calendar notifications"}
							>
							</BaseCheckbox>
							)}
					</div>

					<button className='base-button csv' onClick={downloadCSV}>Download job history (CSV)</button>

				</>
			}
			<div className="bottom">
				{error.message && (
					<div className="error">
						<b>{error.type}:</b> {error.message}
					</div>
				)}
			</div>
			<Loader show={isLoading} />
			<Completed show={isSaved} setShow={setIsSaved} />
		</div>
	);
};

export default observer(TabGeneral);